import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import * as PupilLinkActions from './pupil-link.actions';
import { PupilLinkService } from '@core/services/pupil-link.service';

/** Reduces the backend's FAILED status code, an HttpErrorResponse or an Error to one message. */
export function extractPupilLinkErrorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error?.status === 'FAILED' && error?.reason) {
    return error.reason;
  }
  if (error?.error?.statusCode?.reason) {
    return error.error.statusCode.reason;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

@Injectable()
export class PupilLinkEffects {
  constructor(private actions$: Actions, private pupilLinkService: PupilLinkService) {}

  loadMismatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PupilLinkActions.loadMismatches),
      switchMap(() =>
        this.pupilLinkService.loadMismatches().pipe(
          map((mismatches) => PupilLinkActions.loadMismatchesSuccess({ mismatches })),
          catchError((error) => of(PupilLinkActions.loadMismatchesFailure({ error: extractPupilLinkErrorMessage(error) })))
        )
      )
    )
  );

  resolveMismatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PupilLinkActions.resolveMismatch),
      mergeMap(({ request }) =>
        this.pupilLinkService.resolve(request).pipe(
          map((mismatchId) => PupilLinkActions.resolveMismatchSuccess({ mismatchId })),
          catchError((error) => of(PupilLinkActions.resolveMismatchFailure({
            mismatchId: request.mismatchId,
            error: extractPupilLinkErrorMessage(error)
          })))
        )
      )
    )
  );

  createPupil$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PupilLinkActions.createPupil),
      mergeMap(({ mismatchId }) =>
        this.pupilLinkService.createPupil({ mismatchId }).pipe(
          map((id) => PupilLinkActions.createPupilSuccess({ mismatchId: id })),
          catchError((error) => of(PupilLinkActions.createPupilFailure({
            mismatchId,
            error: extractPupilLinkErrorMessage(error)
          })))
        )
      )
    )
  );

  dismissMismatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PupilLinkActions.dismissMismatch),
      mergeMap(({ mismatchId }) =>
        this.pupilLinkService.dismiss({ mismatchId }).pipe(
          map((id) => PupilLinkActions.dismissMismatchSuccess({ mismatchId: id })),
          catchError((error) => of(PupilLinkActions.dismissMismatchFailure({
            mismatchId,
            error: extractPupilLinkErrorMessage(error)
          })))
        )
      )
    )
  );
}
